import { useState, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { Link } from "react-router-dom";

import {
    Container,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Box,
    Alert,
    CircularProgress,
    Grid,
    Card,
    CardContent
} from '@mui/material';

import Home from '@mui/icons-material/Home';
import Telegram from '@mui/icons-material/Telegram';

import { callSubmitGitHubIssue } from "../../api.js";

import { useAuth } from '../../AuthContext.jsx';

export const Issue = () => {
    const [ result, setResult ] = useState(null);
    const [ issue, setIssue ] = useState({ email: '', type: '', desc: '' });
    const [ user, setUser ] = useState(null);
    const [ authLoading, setAuthLoading ] = useState(true);
    const [ submitting, setSubmitting ] = useState(false);

    const { auth, token } = useAuth();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setAuthLoading(false);
            if (currentUser) {
                setIssue(prev => ({ ...prev, email: currentUser.email }));
            }
        });

        return () => unsubscribe();
    }, [ auth ]);

    const handleSubmitNewIssue = async () => {
        if (!issue.email || !issue.type || !issue.desc) {
            setResult({ type: 'error', message: 'Please fill in all required fields.' });

            return;
        }

        setSubmitting(true);
        setResult(null);

        try {
            const ret = await callSubmitGitHubIssue(issue.email, issue.type, issue.desc, token);

            if (ret.error) {
                setResult({ type: 'error', message: ret.error });
            } else {
                setResult({ type: 'success', message: 'Issue submitted!' });
                setIssue({ email: issue.email, type: '', desc: '' }); // Keep email, clear others
            }
        } catch (error) {
            setResult({ type: 'error', message: error.message });
        } finally {
            setSubmitting(false);
        }
    };

    if (authLoading) {
        return (
            <Container maxWidth="md" sx={ { display: 'flex', justifyContent: 'center', mt: 4 } }>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Box sx={ { minHeight: '100vh' } }>
            <Container maxWidth="md" sx={ { py: 4 } }>
                <Grid container spacing={ 3 }>
                    <Grid size={ { xs: 12 } }>
                        <Typography variant="h4" component="h1" gutterBottom align="center">
                            Report an Issue
                        </Typography>
                        <Typography variant="body1" color="text.secondary" align="center" sx={ { mb: 4 } }>
                            Help us improve Patrol360 by reporting any issues you encounter.
                        </Typography>
                    </Grid>

                    <Grid size={ { xs: 12 } }>
                        <Card elevation={ 2 }>
                            <CardContent sx={ { p: 4 } }>
                                <Typography variant="h6" gutterBottom>
                                    Issue Details
                                </Typography>

                                <Box component="form" sx={ { mt: 2 } }>
                                    <Grid container spacing={ 3 }>
                                        <Grid size={ { xs: 12, md: 6 } }>
                                            <TextField
                                                fullWidth
                                                label="Email Address"
                                                type="email"
                                                value={ issue.email }
                                                onChange={ (e) => setIssue({ ...issue, email: e.target.value }) }
                                                disabled={ !!user?.email }
                                                required
                                                helperText={ user?.email ? "Using your account email" : "" }
                                            />
                                        </Grid>

                                        <Grid size={ { xs: 12, md: 6 } }>
                                            <FormControl fullWidth required>
                                                <InputLabel>Issue Type</InputLabel>
                                                <Select
                                                    value={ issue.type }
                                                    label="Issue Type"
                                                    onChange={ (e) => setIssue({ ...issue, type: e.target.value }) }
                                                >
                                                    <MenuItem value="">
                                                        <em>Select an issue type</em>
                                                    </MenuItem>
                                                    <MenuItem value="password">Password Issues</MenuItem>
                                                    <MenuItem value="email verification">Email Verification</MenuItem>
                                                    <MenuItem value="guide">User Guide</MenuItem>
                                                    <MenuItem value="other">Other</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid size={ { xs: 12 } }>
                                            <TextField
                                                fullWidth
                                                label="Description"
                                                multiline
                                                rows={ 4 }
                                                value={ issue.desc }
                                                onChange={ (e) => setIssue({ ...issue, desc: e.target.value }) }
                                                placeholder="Please describe the issue in detail..."
                                                required
                                                helperText="Include steps to reproduce and any error messages"
                                            />
                                        </Grid>

                                        <Grid size={ { xs: 12 } }>
                                            <Box sx={ { display: 'flex', gap: 2, justifyContent: 'center' } }>
                                                <Button
                                                    variant="contained"
                                                    size="large"
                                                    onClick={ handleSubmitNewIssue }
                                                    disabled={ submitting || !issue.email || !issue.type || !issue.desc }
                                                    sx={ { minWidth: 120 } }
                                                    endIcon={ <Telegram /> }
                                                    loading={ submitting }
                                                    loadingPosition="end"
                                                >
                                                    send
                                                </Button>

                                                <Button
                                                    component={ Link }
                                                    to="/"
                                                    variant="outlined"
                                                    size="large"
                                                    endIcon={ <Home /> }
                                                >
                                                    back
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>

                                { result && (
                                    <Alert
                                        severity={ result.type }
                                        sx={ { mt: 3 } }
                                        onClose={ () => setResult(null) }
                                    >
                                        { result.message }
                                    </Alert>
                                ) }
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};